import React, {useState} from "react";
import {useOrderApi} from "../context/ApiContext";
import SamButton from "./SamButton";
import {OrderDetails, ScheduleInspectionFormData} from "../api/ApiTypes.ts";
import {createLocalDate, formatDateToString} from "../utils/dateUtils.ts";
import CancelButton from "./CancelButton.tsx";

interface ScheduleInspectionFormProps {
    myOrderDetails: OrderDetails | null;
    onSuccess?: () => void;
    onCancel?: () => void;
    inspectionActualDate?: string;
    estimatedDeliveryDate?: string;
    inspectionDueDate: string;
    wasPreviouslyScheduled: boolean;
}

export const ScheduleInspectionForm: React.FC<ScheduleInspectionFormProps> = ({
                                                                                  myOrderDetails,
                                                                                  onSuccess,
                                                                                  onCancel,
                                                                                  inspectionActualDate,
                                                                                  estimatedDeliveryDate,
                                                                                  inspectionDueDate,
                                                                                  wasPreviouslyScheduled,
                                                                              }) => {

    if (!myOrderDetails || !myOrderDetails.id) {
        return <div>Invalid Order ID</div>;
    }

    const minInspectionDateValue = formatDateToString(createLocalDate(myOrderDetails.createDate));

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const checkIfLateInspection = (inspectionDate: Date): boolean => {
        if (inspectionDate && inspectionDueDate) {
            //returns true if inspection date is after due date
            return inspectionDate > createLocalDate(inspectionDueDate) && !wasPreviouslyScheduled;
        }
        else {
            return false;
        }
    };

    const [isLateInspection, setIsLateInspection] = useState<boolean>(checkIfLateInspection(createLocalDate(inspectionActualDate)));
    const orderApi = useOrderApi();
    const [isVacant, setIsVacant] = useState<boolean>(false);

    const [formData, setFormData] = useState<ScheduleInspectionFormData>({
        orderId: myOrderDetails.id,
        inspectionActualDate: formatDateToString(createLocalDate(inspectionActualDate)),
        estimatedDeliveryDate: formatDateToString(createLocalDate(estimatedDeliveryDate)),
        lateInspectionReason: "",
        appointmentStartTime: "",
        appointmentEndTime: "",
        appointmentScheduledWith: "",
        rescheduleReason: ""
    });

    const [errors, setErrors] = useState<{
        inspectionDate?: string;
        estimatedDeliveryDate?: string;
        lateInspectionReason?: string;
        appointmentStartTime?: string;
        appointmentEndTime?: string;
        appointmentScheduledWith?: string;
        rescheduleReason?: string;
    }>({});

    // Handler for date changes using date picker
    const handleInspectionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = createLocalDate(e.target.value);
        const isLate = checkIfLateInspection(newDate);
        setFormData(prev => ({
            ...prev,
            inspectionActualDate: formatDateToString(newDate),
        }));
        setIsLateInspection(isLate);
    };

    const handleDeliveryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = createLocalDate(e.target.value);
        // Update the form data with formatted date
        setFormData(prev => ({
            ...prev,
            estimatedDeliveryDate: formatDateToString(newDate),
        }));
    };

    // Handle vacant property checkbox
    const handleVacantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIsVacant(isChecked);

        if (isChecked) {
            // Set fields for vacant property
            setFormData(prev => ({
                ...prev,
                appointmentScheduledWith: "Vacant",
                appointmentStartTime: "09:00:00",
                appointmentEndTime: "17:00:00"
            }));
        } else {
            // Clear fields when unchecked
            setFormData(prev => ({
                ...prev,
                appointmentScheduledWith: "",
                appointmentStartTime: "",
                appointmentEndTime: ""
            }));
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            let updated = { ...prev, [name]: value };

            if (name === "appointmentStartTime" && value) {
                const startTime = new Date(`1970-01-01T${value}`);
                const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

                const formattedEndTime = endTime.toTimeString().slice(0, 8);

                if (formattedEndTime <= "20:00:00") {
                    updated.appointmentEndTime = formattedEndTime;
                } else {
                    updated.appointmentEndTime = "";
                }
            }

            return updated;
        });

         // Clear error when user starts typing in late inspection reason
        if (name === "lateInspectionReason" && value.trim()) {
            setErrors((prev) => ({ ...prev, lateInspectionReason: undefined }));
        } else if (name === "rescheduleReason" && value.trim()) {
            setErrors((prev) => ({ ...prev, rescheduleReason: undefined }));
        }
    };


    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors: {
            inspectionDate?: string;
            estimatedDeliveryDate?: string;
            lateInspectionReason?: string;
            appointmentStartTime?: string;
            appointmentEndTime?: string;
            appointmentScheduledWith?: string;
            rescheduleReason?: string;
        } = {};

        // Check required fields
        if (!formData.inspectionActualDate) {
            newErrors.inspectionDate = "Inspection date is required";
            isValid = false;
        }

        if (!formData.estimatedDeliveryDate) {
            newErrors.estimatedDeliveryDate = "Estimated delivery date is required";
            isValid = false;
        }

        if (formData.estimatedDeliveryDate < formData.inspectionActualDate) {
            newErrors.estimatedDeliveryDate = "Estimated delivery date cannot be before Inspection Date";
            isValid = false;
        }

        if (!formData.appointmentStartTime) {
            newErrors.appointmentStartTime = "Appointment start time is required";
            isValid = false;
        }

        if (!formData.appointmentEndTime) {
            newErrors.appointmentEndTime = "Appointment end time is required";
            isValid = false;
        }

        if (formData.appointmentEndTime && formData.appointmentStartTime) {
            const startTime = convertTimeToMinutes(formData.appointmentStartTime);
            const endTime = convertTimeToMinutes(formData.appointmentEndTime);

            if (startTime >= endTime) {
                newErrors.appointmentEndTime = "End time must be after start time";
                isValid = false;
            }
        }

        if (!formData.appointmentScheduledWith) {
            newErrors.appointmentScheduledWith = "Scheduled with is required";
            isValid = false;
        }

        // Validate late inspection reason if inspection date is after due date
        if (isLateInspection && !formData.lateInspectionReason.trim()) {
            newErrors.lateInspectionReason = "Please provide a reason for the late inspection";
            isValid = false;
        }

        if (wasPreviouslyScheduled && !formData.rescheduleReason.trim()) {
            newErrors.rescheduleReason = "Please provide a reason for Rescheduling";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const convertTimeToMinutes = (timeString: string) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            // Call your API to schedule inspection
            await orderApi.scheduleInspection(formData);

            // Call onSuccess to notify parent component if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Error scheduling inspection:", error);
            // Handle API error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Prevent cancellation during submission
        if (!isSubmitting && onCancel) {
            onCancel();
        }
    };


    return (
        <div className="w-full max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="hidden" name="orderId" value={formData.orderId} />

                <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="schedule-inspection-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Inspection Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            required
                            type="date"
                            id="schedule-inspection-date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.inspectionActualDate}
                            min={minInspectionDateValue}
                            onChange={handleInspectionDateChange}
                        />
                        {errors.inspectionDate && (
                            <p className="mt-1 text-sm text-red-600">{errors.inspectionDate}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="estimated-delivery-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Estimated Delivery Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            required
                            type="date"
                            id="estimated-delivery-date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.estimatedDeliveryDate}
                            onChange={handleDeliveryDateChange}
                        />
                        {errors.estimatedDeliveryDate && (
                            <p className="mt-1 text-sm text-red-600">{errors.estimatedDeliveryDate}</p>
                        )}
                    </div>
                </fieldset>

                <div className="mt-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={isVacant}
                            onChange={handleVacantChange}
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Vacant Property</span>
                    </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="appointment-start-time" className="block text-sm font-medium text-gray-700 mb-1">
                            Appointment Start Time <span className="text-red-500">*</span>
                        </label>
                        <select
                            required
                            id="appointment-start-time"
                            name="appointmentStartTime"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.appointmentStartTime}
                            onChange={handleChange}
                            disabled={isVacant}
                        >
                            <option value=""></option>
                            <option value="07:00:00">7:00am</option>
                            <option value="07:30:00">7:30am</option>
                            <option value="08:00:00">8:00am</option>
                            <option value="08:30:00">8:30am</option>
                            <option value="09:00:00">9:00am</option>
                            <option value="09:30:00">9:30am</option>
                            <option value="10:00:00">10:00am</option>
                            <option value="10:30:00">10:30am</option>
                            <option value="11:00:00">11:00am</option>
                            <option value="11:30:00">11:30am</option>
                            <option value="12:00:00">12:00pm</option>
                            <option value="12:30:00">12:30pm</option>
                            <option value="13:00:00">1:00pm</option>
                            <option value="13:30:00">1:30pm</option>
                            <option value="14:00:00">2:00pm</option>
                            <option value="14:30:00">2:30pm</option>
                            <option value="15:00:00">3:00pm</option>
                            <option value="15:30:00">3:30pm</option>
                            <option value="16:00:00">4:00pm</option>
                            <option value="16:30:00">4:30pm</option>
                            <option value="17:00:00">5:00pm</option>
                            <option value="17:30:00">5:30pm</option>
                            <option value="18:00:00">6:00pm</option>
                            <option value="18:30:00">6:30pm</option>
                            <option value="19:00:00">7:00pm</option>
                            <option value="19:30:00">7:30pm</option>
                            <option value="20:00:00">8:00pm</option>
                        </select>
                        {errors.appointmentStartTime && (
                            <p className="mt-1 text-sm text-red-600">{errors.appointmentStartTime}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="appointment-end-time" className="block text-sm font-medium text-gray-700 mb-1">
                            Appointment End Time <span className="text-red-500">*</span>
                        </label>
                        <select
                            required
                            id="appointment-end-time"
                            name="appointmentEndTime"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            value={formData.appointmentEndTime}
                            onChange={handleChange}
                            disabled={isVacant}
                        >
                            <option value=""></option>
                            <option value="07:00:00">7:00am</option>
                            <option value="07:30:00">7:30am</option>
                            <option value="08:00:00">8:00am</option>
                            <option value="08:30:00">8:30am</option>
                            <option value="09:00:00">9:00am</option>
                            <option value="09:30:00">9:30am</option>
                            <option value="10:00:00">10:00am</option>
                            <option value="10:30:00">10:30am</option>
                            <option value="11:00:00">11:00am</option>
                            <option value="11:30:00">11:30am</option>
                            <option value="12:00:00">12:00pm</option>
                            <option value="12:30:00">12:30pm</option>
                            <option value="13:00:00">1:00pm</option>
                            <option value="13:30:00">1:30pm</option>
                            <option value="14:00:00">2:00pm</option>
                            <option value="14:30:00">2:30pm</option>
                            <option value="15:00:00">3:00pm</option>
                            <option value="15:30:00">3:30pm</option>
                            <option value="16:00:00">4:00pm</option>
                            <option value="16:30:00">4:30pm</option>
                            <option value="17:00:00">5:00pm</option>
                            <option value="17:30:00">5:30pm</option>
                            <option value="18:00:00">6:00pm</option>
                            <option value="18:30:00">6:30pm</option>
                            <option value="19:00:00">7:00pm</option>
                            <option value="19:30:00">7:30pm</option>
                            <option value="20:00:00">8:00pm</option>
                        </select>
                        {errors.appointmentEndTime && (
                            <p className="mt-1 text-sm text-red-600">{errors.appointmentEndTime}</p>
                        )}
                    </div>
                </div>

                <div>
                    <label htmlFor="scheduled-with" className="block text-sm font-medium text-gray-700 mb-1">
                        Scheduled With <span className="text-red-500">*</span>
                    </label>
                    <select
                        required
                        id="scheduled-with"
                        name="appointmentScheduledWith"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={formData.appointmentScheduledWith}
                        onChange={handleChange}
                        disabled={isVacant}
                    >
                        <option value=""></option>
                        <option value="Borrower">Borrower</option>
                        <option value="Entry Contact">Entry Contact</option>
                        <option value="Realtor">Realtor</option>
                        <option value="Tenant">Tenant</option>
                        <option value="Vacant">Vacant</option>
                    </select>
                    {errors.appointmentScheduledWith && (
                        <p className="mt-1 text-sm text-red-600">{errors.appointmentScheduledWith}</p>
                    )}
                </div>

                {wasPreviouslyScheduled && (
                    <>
                        <label htmlFor="reasonForReschedule" className="block text-sm font-medium text-gray-700 mb-1">
                            Reschedule Reason<span className="text-red-500">*</span>
                        </label>
                        <textarea
                            required
                            id="reasonForReschedule"
                            name="rescheduleReason"
                            rows={5}
                            className={`w-full px-3 py-2 border ${errors.rescheduleReason ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                            placeholder="Enter your reasons here..."
                            value={formData.rescheduleReason}
                            onChange={handleChange}
                        />
                        {errors.rescheduleReason && (
                            <p className="mt-1 text-sm text-red-600">{errors.rescheduleReason}</p>
                        )}
                    </>
                )}

                {isLateInspection && (
                    <div>
                        <label htmlFor="lateInspectionReason" className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Late Inspection <span className="text-red-500">*</span>
                        </label>
                        <input
                            required
                            type="text"
                            id="lateInspectionReason"
                            name="lateInspectionReason"
                            className={`w-full px-3 py-2 border ${errors.lateInspectionReason ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                            value={formData.lateInspectionReason}
                            onChange={handleChange}
                        />
                        {errors.lateInspectionReason && (
                            <p className="mt-1 text-sm text-red-600">{errors.lateInspectionReason}</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end space-x-2">
                    <CancelButton
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </CancelButton>
                    <SamButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Saving...</span>
                            </>
                        ) : wasPreviouslyScheduled ? "Reschedule" : "Schedule"}
                    </SamButton>
                </div>
            </form>
        </div>
    );
};